import { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode, useCallback, forwardRef } from 'react';
import { getCornerStyleClassname, IconType, Size } from '../../../constants';
import classNames from 'classnames';
import { BUTTON_ANIMATION, getButtonColorClasses, getButtonSizeClasses } from './constants';
import { MotionProps, m } from 'framer-motion';
import { ButtonType } from './ButtonType';
import { Tooltip } from '../tooltip/Tooltip';
import { useTheme } from '../../../context/theme/useTheme';
import { ThemeBackground, ThemePrimary, ThemeSecondary } from '../../../context/theme/ThemeConstantsUpdated';

const { mainBackground } = ThemeBackground;
const { secondaryBackground } = ThemeSecondary;
const { primaryShadow } = ThemePrimary;

export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(function Button(props, ref) {
  const {
    onClick,
    children,
    Icon,
    className,
    buttonType,
    options = {},
    href,
    target,
    rel,
    iconColorOverride,
    ...rest
  } = props;
  const {
    size = Size.md,
    includeShadow = false,
    includeAnimation = false,
    includeNotificationAlert = false,
    tooltip,
    cornerStyle = Size.sm,
    isFullWidth = false,
  } = options;
  const { isDarkMode } = useTheme();
  const { disabled } = rest;
  const isLink = buttonType?.toLocaleLowerCase().includes('link');
  const cornerStyleClassName = getCornerStyleClassname(cornerStyle);

  const Btn = useCallback(() => {
    const colorClasses = getButtonColorClasses({
      buttonType,
      isDarkMode,
    });
    const { buttonSizeClasses } = getButtonSizeClasses(size, isLink);
    const compositeChildren = (
      <>
        {Icon && (
          <Icon
            className={classNames('-ml-0.5', size === Size.lg ? 'size-6' : 'size-5', iconColorOverride)}
            aria-hidden="true"
          />
        )}
        {children}
      </>
    );

    const Element: any = href ? m.a : m.button;

    const commonProps = {
      onClick,
      className: classNames(
        className,
        colorClasses,
        buttonSizeClasses,
        cornerStyleClassName,
        size !== Size.lg ? 'text-sm' : 'text-lg',
        { 'w-full': isFullWidth },
        includeShadow && primaryShadow,
        !isLink && 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
        'inline-flex items-center gap-x-1.5 font-semibold'
      ),
      ...(includeAnimation ? BUTTON_ANIMATION : {}),
      ref,
    } as any;

    return (
      <Element {...commonProps} {...(href ? { href, target, rel } : { type: 'button' })} {...rest}>
        {isFullWidth ? <div className="mx-auto">{compositeChildren}</div> : compositeChildren}
      </Element>
    );
  }, [
    buttonType,
    isDarkMode,
    size,
    isLink,
    Icon,
    children,
    onClick,
    className,
    cornerStyleClassName,
    isFullWidth,
    includeShadow,
    includeAnimation,
    rest,
    href,
    target,
    rel,
    ref,
  ]);

  const TooltipedButton = useCallback(() => {
    return tooltip != null ? (
      <Tooltip tip={tooltip}>
        <Btn />
      </Tooltip>
    ) : (
      <Btn />
    );
  }, [Btn, tooltip]);

  return includeNotificationAlert || disabled ? (
    <div className={classNames('relative', { 'w-full': isFullWidth })}>
      <TooltipedButton />
      {disabled && (
        <div
          className={classNames(
            cornerStyleClassName,
            mainBackground,
            'absolute left-0 top-0 h-full w-full bg-opacity-50'
          )}
        />
      )}
      {includeNotificationAlert && (
        <span
          className={classNames(
            includeNotificationAlert === 'light' ? 'bg-white' : secondaryBackground,
            'absolute -right-0.5 -top-0.5 inline-flex h-3 w-3 rounded-full'
          )}
        />
      )}
    </div>
  ) : (
    <TooltipedButton />
  );
});

export type ButtonProps = MotionProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> &
  Partial<AnchorHTMLAttributes<HTMLAnchorElement>> & {
    onClick?: (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
    children: ReactNode;
    Icon?: IconType;
    buttonType?: ButtonType;
    options?: ButtonOptions;
    href?: string;
    target?: string;
    rel?: string;
    iconColorOverride?: string;
  };

type ButtonOptions = {
  size?: Size.sm | Size.lg | Size.xl;
  isFullWidth?: boolean;
  includeShadow?: boolean;
  includeAnimation?: boolean;
  includeNotificationAlert?: 'light' | boolean;
  tooltip?: string;
  cornerStyle?: Size;
};
